import { QualityRegimeOptions, wellFunctionOptions } from "@nens/brostar/uploadapi-client";
import {
  designLoopTypeOptions,
  designScreenGeometryOptions,
  displacementDirectionOptions,
  filterTypeOptions,
  gufDeliveryContextOptions,
  indicationYesNoOptions,
  installationFunctionOptions,
  legalTypeOptions,
  qualityRegimeOptions,
  relativeTemperatureOptions,
  usageTypeOptions,
} from "@nens/brostar/uploadapi-client";

import { BrostarUiSchema, getEnumNamesAndDisabled } from "@/libs/brostar-form";

/**
 * UI Schema library for GUF forms with reusable field configurations
 */
export const createGufUiSchemaLibrary = (qualityRegime: QualityRegimeOptions | undefined) =>
  ({
    // Metadata fields
    projectNumber: {},
    qualityRegime: getEnumNamesAndDisabled(qualityRegimeOptions, qualityRegime),
    deliveryAccountableParty: {
      "ui:widget": "OrganisationSelect" as const,
    },
    broId: {},
    correctionReason: {},

    // GUF StartRegistration top-level fields
    objectIdAccountableParty: {},
    deliveryContext: getEnumNamesAndDisabled(gufDeliveryContextOptions, qualityRegime),
    startTime: {},
    identificationLicence: {},
    legalType: getEnumNamesAndDisabled(legalTypeOptions, qualityRegime),
    primaryUsageType: getEnumNamesAndDisabled(usageTypeOptions, qualityRegime),
    secondaryUsageTypes: getEnumNamesAndDisabled(usageTypeOptions, qualityRegime, 5),
    humanConsumption: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),

    // LicensedQuantity fields
    licensedInOut: getEnumNamesAndDisabled(displacementDirectionOptions, qualityRegime),
    maximumPerHour: {},
    maximumPerDay: {},
    maximumPerMonth: {},
    maximumPerQuarter: {},
    maximumPerYear: {},

    // DesignInstallation fields
    designInstallationId: {},
    installationFunction: getEnumNamesAndDisabled(installationFunctionOptions, qualityRegime),
    designInstallationPos: {},
    energyCharacteristics: {},

    // EnergyCharacteristics fields
    energyCold: {},
    energyWarm: {},
    maximumInfiltrationTemperatureWarm: {},
    averageInfiltrationTemperatureCold: {},
    averageInfiltrationTemperatureWarm: {},
    powerCold: {},
    powerWarm: {},
    power: {},
    averageColdWater: {},
    averageWarmWater: {},
    maximumYearQuantityCold: {},
    maximumYearQuantityWarm: {},

    // DesignWell fields
    designWellId: {},
    wellFunctions: getEnumNamesAndDisabled(wellFunctionOptions, qualityRegime), // Is possibly an array of options import many to many relationships, so no enum here
    height: {},
    wellPos: {},
    geometryPubliclyAvailable: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),
    maximumWellDepth: {},
    maximumWellDepthPubliclyAvailable: getEnumNamesAndDisabled(
      indicationYesNoOptions,
      qualityRegime
    ),
    maximumWellCapacity: {},
    relativeTemperature: getEnumNamesAndDisabled(relativeTemperatureOptions, qualityRegime),

    // DesignScreen fields
    screenType: getEnumNamesAndDisabled(filterTypeOptions, qualityRegime),
    designScreenTop: {},
    designScreenBottom: {},
    designScreenGeometry: getEnumNamesAndDisabled(designScreenGeometryOptions, qualityRegime),

    // DesignLoop fields
    designLoopId: {},
    designLoopPos: {},
    loopType: getEnumNamesAndDisabled(designLoopTypeOptions, qualityRegime),
    startDate: {},
    endDate: {},
    geometryType: {},

    // DesignSurfaceInfiltration fields
    designSurfaceInfiltrationId: {},
    designSurfaceInfiltrationPos: {},

    // ── Realised installation fields ─────────────────────────────────────
    realisedInstallationId: {},
    realisedLoopPos: {},
    startValidity: {},

    // ── Realised well fields ─────────────────────────────────────────────
    realisedWellId: {},
    wellDepth: {},
    publiclyAvailable: getEnumNamesAndDisabled(indicationYesNoOptions, qualityRegime),
    validity: {},
    lifespan: {},

    // ── Realised loop fields ─────────────────────────────────────────────
    realisedLoopId: {},
    endDepthBottom: {},
    segments: {},
    realisedGeometryType: {},

    // ── Realised screen fields ───────────────────────────────────────────
    realisedScreenId: {},
    topScreenDepth: {},
    length: {},
    realisedScreenPos: {},

    // ── Realised surface infiltration fields ─────────────────────────────
    realisedSurfaceInfiltrationId: {},
    realisedSurfaceInfiltrationPos: {},

    // ── Event / date fields ──────────────────────────────────────────────
    eventDate: {},
    endTime: {},

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } satisfies Record<string, BrostarUiSchema<{ [key: string]: any }>[string]>);

export type GufUiSchemaLibrary = ReturnType<typeof createGufUiSchemaLibrary>;
